// src/pages/organization/team/PlayerProfile.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMainPageTitle } from "../../../contexts/PageContext";
import ProfilePicture from "../../../components/profileImage";
import TabComponent from "../../../components/common/TabComponent";
import DeleteConfirmationDialog from "../../../components/common/DeleteConfirmationDialog";
import Popup from "../../../components/popup";
import OverviewTab from './tabs/OverviewTab';
import PerformanceTab from './tabs/PerformanceTab';
import EventsTab from './tabs/EventsTab';
import ParentTab from './tabs/ParentTab';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const PlayerProfile = () => {
    useMainPageTitle("Player Profile", "Player Profile");
    const { playerId } = useParams();
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [isAddPerformancePopupOpen, setIsAddPerformancePopupOpen] = useState(false);

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'performance', label: 'Performance' },
        { id: 'events', label: 'Events' },
        { id: 'parent', label: 'Parent' }
    ];

    useEffect(() => {
        const fetchPlayer = async () => {
            try {
                if (!API_URL) {
                    throw new Error('API_URL is not defined');
                }
                const response = await fetch(`${API_URL}/user/?id=${playerId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (!response.ok) { 
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setPlayer(data.user);
                console.log(data.user.metaData);
                const teamData = await fetch(`${API_URL}/team/show-team-by-team-id/?teamId=${data.user.metaData.team}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (!teamData.ok) {
                    throw new Error(`HTTP error! status: ${teamData.status}`);
                }
                const team = await teamData.json();
                setTeam(team.team);
            } catch (error) {   
                setError(error);
                console.error('Error fetching player:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPlayer();
    }, [playerId]);

    const handleDeleteFromTeam = async () => {
        try {
            const response = await fetch(`${API_URL}/team/delete-player-from-team-by-organization/?playerId=${playerId}&teamId=${team._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) throw new Error('Failed to remove player');
            navigate(`/organization/team/${team._id}`);
        } catch (error) {
            console.error('Error removing player:', error);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
    if (error) return <div className="text-red-500 text-center">{error.message}</div>;
    if (!player) return <div className="text-center">Player not found</div>;

    return (
        <div className="py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {/* Header with background */}
                    <div className="bg-gradient-to-r from-[#F15213] to-[#F15213] px-6 py-16 relative">
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                            <ProfilePicture
                                image={player.profilePicture}
                                alt={`${player.firstName} ${player.lastName}`}
                                className="rounded-full object-cover w-20 h-20"
                                myUser={player}
                            />
                        </div>
                    </div>

                    {/* Profile info */}
                    <div className="pt-16 px-6 pb-6 text-center">
                        <h1 className="text-2xl font-bold text-gray-900">
                            {player.firstName} {player.lastName}
                        </h1>
                        <p className="text-gray-500">@{player.userName}</p>
                        <p className="mt-1 text-sm text-gray-600">{player.accountType}</p>

                        <div className="mt-4 space-x-4">
                            <button
                                onClick={() => navigate(`/chat/${player.id}`)}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                            >
                                Send Message
                            </button>
                            <button
                                onClick={() => setIsDeletePopupOpen(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                            >
                                Delete from Team
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="px-6">
                        <TabComponent
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                        />
                    </div>

                    {/* Tab Content */}
                    <div className="px-6 py-4">
                        {activeTab === 'overview' && <OverviewTab player={player} team={team} />}
                        {activeTab === 'performance' && <PerformanceTab playerId={playerId} team={team} />}
                        {activeTab === 'events' && <EventsTab playerId={playerId} team={team} />}
                        {activeTab === 'parent' && <ParentTab playerId={playerId} team={team} />}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                isOpen={isDeletePopupOpen}
                onClose={() => setIsDeletePopupOpen(false)}
                onConfirm={handleDeleteFromTeam}
                title="Remove Player from Team"
                message={`Are you sure you want to remove ${player.firstName} ${player.lastName} from the team?`}
                confirmButtonText="Remove"
            />

            {/* Add Performance Popup */}
            <Popup
                isOpen={isAddPerformancePopupOpen}
                onClose={() => setIsAddPerformancePopupOpen(false)}
                onConfirm={() => {/* Handle adding performance */}}
                message="Add Performance Document"
            />
        </div>
    );
};

export default PlayerProfile;